import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { type Board, useCreateBoard } from '@/entities/board';
import {
  type AccessGrant,
  ASSIGNABLE_ROLES,
  type MemberRole,
  RoleGrantsField,
  roleAccessBody,
  TeamGrantsField,
  teamAccessBody,
  UserSelect,
  useBoardLabelCatalog,
  useTeams,
  useWorkspaceMembers,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { useWorkspace } from '@/entities/workspace';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import {
  Button,
  Combobox,
  DialogActions,
  IconButton,
  Icons,
  MultiSelect,
  TextAreaField,
  TextField,
} from '@/shared/ui';

import styles from './BoardManage.module.css';

const schema = z.object({
  name: z.string().trim().min(1, 'REQUIRED').max(200, 'STRING_TOO_LONG'),
  description: z.string().max(5000, 'STRING_TOO_LONG'),
});
type Values = z.infer<typeof schema>;

type Grant = AccessGrant;

/**
 * A board is private by default: only its owner (the creator) and the workspace owner see it.
 * In a TEAM workspace the creator can set up the initial collaboration at once - the team the
 * board is created for (succession, not access), labels, teams with access and participants -
 * and everything is created in one transaction, so nobody sees a half-configured board.
 */
export function CreateBoardForm({
  workspaceId,
  sectionId,
  groupId,
  onCreated,
  onCancel,
}: {
  workspaceId: string;
  /** File the new board in this section in the same request (UD-STRUCT). */
  sectionId?: string | null | undefined;
  /** Inside this group (it decides the section). */
  groupId?: string | null | undefined;
  onCreated: (board: Board) => void;
  /** When given, a Cancel button sits next to Create (dialog use). */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const create = useCreateBoard(workspaceId);
  const workspace = useWorkspace(workspaceId);
  const isTeam = workspace.data?.workspace_type === 'team';
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const teams = useTeams(workspaceId, isTeam);
  const members = useWorkspaceMembers(workspaceId, isTeam);
  const labels = useBoardLabelCatalog(workspaceId, isTeam);
  const [origin, setOrigin] = useState<string | null>(null);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [teamAccess, setTeamAccess] = useState<Grant[]>([]);
  const [participants, setParticipants] = useState<Grant[]>([]);
  const [roleAccess, setRoleAccess] = useState<Grant[]>([]);
  const [personPick, setPersonPick] = useState<string | null>(null);
  const { register, handleSubmit, formState, setError, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });
  const serverErrors = useServerErrors(setError, ['name']);

  const mayManageTeams = can(workspace.data?.permissions, PERMISSIONS.workspaceManageTeams);
  const ownTeams = (teams.data ?? []).filter(
    (team) => mayManageTeams || (viewerId !== null && team.member_ids.includes(viewerId)),
  );
  const personName = (id: string) =>
    (members.data ?? []).find((x) => x.user_id === id)?.display_name ?? '…';

  const onSubmit = handleSubmit((values) => {
    serverErrors.clear();
    create.mutate(
      {
        name: values.name,
        ...(sectionId && { section_id: sectionId }),
        ...(groupId && { group_id: groupId }),
        ...(isTeam && values.description.trim() && { description: values.description.trim() }),
        ...(isTeam && origin !== null && { origin_team_id: origin }),
        ...(isTeam && labelIds.length > 0 && { label_ids: labelIds }),
        ...(isTeam && teamAccess.length > 0 && { team_access: teamAccessBody(teamAccess) }),
        ...(isTeam && roleAccess.length > 0 && { role_access: roleAccessBody(roleAccess) }),
        ...(isTeam &&
          participants.length > 0 && {
            participants: participants.map((g) => ({ user_id: g.id, role: g.role })),
          }),
      },
      {
        onSuccess: (board) => {
          reset();
          onCreated(board);
        },
        onError: serverErrors.handle,
      },
    );
  });

  const roleOptions = ASSIGNABLE_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }));

  return (
    <form className={styles.createForm} onSubmit={(e) => void onSubmit(e)} noValidate>
      <TextField
        label={t('boards.newBoardName')}
        autoFocus
        placeholder={t('boards.newBoardPlaceholder')}
        error={fieldError(formState.errors.name?.message)}
        {...register('name')}
      />
      {isTeam && (
        <div className={styles.teamOptions}>
          <TextAreaField label={t('boards.description')} rows={3} {...register('description')} />

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.originTeam')}</span>
            <p className={styles.optionHint}>{t('boards.originTeamHint')}</p>
            <Combobox
              label={t('boards.originTeam')}
              value={origin ?? '__none__'}
              size="sm"
              searchPlaceholder={t('common.search')}
              emptyText={t('common.noResults')}
              options={[
                { value: '__none__', label: t('boards.noOriginTeam') },
                ...ownTeams.map((team) => ({ value: team.id, label: team.name })),
              ]}
              onChange={(next) => {
                setOrigin(next === '__none__' ? null : next);
              }}
            />
          </div>

          {(labels.data ?? []).length > 0 && (
            <div className={styles.optionBlock}>
              <span className={styles.optionTitle}>{t('boards.initialLabels')}</span>
              <MultiSelect
                label={t('boards.initialLabels')}
                options={(labels.data ?? []).map((label) => ({
                  value: label.id,
                  label: label.name,
                  color: label.color,
                }))}
                value={labelIds}
                onChange={setLabelIds}
              />
            </div>
          )}

          <TeamGrantsField
            workspaceId={workspaceId}
            enabled={isTeam}
            value={teamAccess}
            onChange={setTeamAccess}
            texts={{
              title: t('boards.teamsWithAccess'),
              hint: t('boards.teamsWithAccessHint'),
              add: t('boards.addTeamAccess'),
              remove: (name) => t('boards.removeGrant', { name }),
            }}
          />

          <RoleGrantsField
            workspaceId={workspaceId}
            enabled={isTeam}
            value={roleAccess}
            onChange={setRoleAccess}
            texts={{
              title: t('boards.roleAccess'),
              hint: t('boards.roleAccessHint'),
              add: t('boards.addRoleAccess'),
              remove: (name) => t('boards.removeGrant', { name }),
            }}
          />

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.initialParticipants')}</span>
            <ul className={styles.grantList}>
              {participants.map((grant) => (
                <li key={grant.id} className={styles.grantRow}>
                  <span className={styles.grantName}>{personName(grant.id)}</span>
                  <Combobox
                    label={t('access.roleOf', { name: personName(grant.id) })}
                    value={grant.role}
                    size="sm"
                    options={roleOptions}
                    onChange={(next) => {
                      setParticipants(
                        participants.map((g) =>
                          g.id === grant.id ? { ...g, role: next as MemberRole } : g,
                        ),
                      );
                    }}
                  />
                  <IconButton
                    label={t('boards.removeGrant', { name: personName(grant.id) })}
                    icon={<Icons.close />}
                    onClick={() => {
                      setParticipants(participants.filter((g) => g.id !== grant.id));
                    }}
                  />
                </li>
              ))}
            </ul>
            <UserSelect
              label={t('boards.addParticipant')}
              users={members.data ?? []}
              value={personPick}
              placeholder={t('boards.addParticipant')}
              exclude={new Set([...(viewerId ? [viewerId] : []), ...participants.map((g) => g.id)])}
              onChange={(next) => {
                setParticipants([...participants, { id: next, role: 'editor' }]);
                setPersonPick(null);
              }}
            />
          </div>
          <p className={styles.optionHint}>{t('boards.privateByDefault')}</p>
        </div>
      )}
      <FormError error={serverErrors.formError} />
      <DialogActions className={styles.createActions}>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {t('boards.create')}
        </Button>
      </DialogActions>
    </form>
  );
}
